import { useCallback } from 'react';
import type { AppliedFilter } from 'store/entities/drawer';
import { isDateFilterOption, isNumberRangeFilterOption } from 'store/entities/drawer';
import type { MappedFilterValue, ThreeLevelsFilterValue, UseMapFilterValuesProps } from 'types';
import type { FilterDate } from 'types/filters';
import { isValueEmpty } from 'utils/filterFunctions';

type FilterHierarchy = {
  mainFormItemId: string;
  nestedFormItemId?: string;
  secondLevelNestedFormItemId?: string;
};

const getFilterHierarchy = (formItemId: string, map: Map<string, string>): FilterHierarchy => {
  const chain: string[] = [formItemId];

  let parent = map.get(formItemId);

  while (parent) {
    chain.unshift(parent);
    parent = map.get(parent);
  }

  return {
    mainFormItemId: chain[0],
    nestedFormItemId: chain.length > 1 ? chain[1] : undefined,
    secondLevelNestedFormItemId: chain.length > 2 ? chain[2] : undefined,
  };
};

const shouldUse3LevelStructure = (map: Map<string, string>): boolean => {
  const parentIds = new Set(map.values());

  for (const parentId of parentIds) {
    if (map.has(parentId)) {
      return true;
    }
  }

  return false;
};

/**
 * Hook for mapping filter values to the correct API structure.
 *
 * Supports two output structures:
 *
 * 1. **2-Level Structure** (for /Records endpoint):
 *    - formItemId: The parent formItemId (or self if no parent)
 *    - referencedFormItemId: The child formItemId (if nested)
 *
 * 2. **3-Level Structure** (for /FilteredRecords endpoint):
 *    - mainFormItemId: The root parent
 *    - nestedFormItemId: First-level nested item
 *    - secondLevelNestedFormItemId: Second-level nested item
 *
 * The structure is automatically detected by checking if any parent ID
 * in the map also appears as a child ID (indicating 3+ levels of nesting).
 *
 * @param referencedFormItemsMap - Map where child formItemId => immediate parent formItemId
 * @param use3LevelStructure - Optional flag to explicitly set which structure to use (auto-detected if omitted)
 */
export const useMapFilterValues = ({ referencedFormItemsMap, use3LevelStructure }: UseMapFilterValuesProps) => {
  const shouldUse3Levels = use3LevelStructure ?? shouldUse3LevelStructure(referencedFormItemsMap);

  const mapFilterValues = useCallback(
    (filterValuesToMap: AppliedFilter[] = []): MappedFilterValue[] | ThreeLevelsFilterValue[] => {
      if (shouldUse3Levels) {
        return filterValuesToMap.map((filterValue) => {
          const filterItemFormItemId = filterValue.formItemId;
          const hierarchy = getFilterHierarchy(filterItemFormItemId, referencedFormItemsMap);

          if (filterValue.formItemType === 'Tables' || filterValue.formItemType === 'nameArray') {
            return {
              mainFormItemId: hierarchy.mainFormItemId,
              nestedFormItemId: hierarchy.nestedFormItemId,
              secondLevelNestedFormItemId: hierarchy.secondLevelNestedFormItemId,
              formItemType: 'Tables',
              values: Array.isArray(filterValue.value)
                ? (filterValue.value as unknown as ({ value: string } | null | undefined)[])
                    .filter((item): item is { value: string } => item != null)
                    .map((item) => item.value)
                : [filterValue.value as string],
            };
          }

          if (filterValue.formItemType === 'Users') {
            return {
              mainFormItemId: hierarchy.mainFormItemId,
              nestedFormItemId: hierarchy.nestedFormItemId,
              secondLevelNestedFormItemId: hierarchy.secondLevelNestedFormItemId,
              formItemType: 'Users',
              values: Array.isArray(filterValue.value) ? (filterValue.value as string[]) : [filterValue.value as string],
            };
          }

          if (filterValue.formItemType === 'Records') {
            return {
              mainFormItemId: hierarchy.mainFormItemId,
              nestedFormItemId: hierarchy.nestedFormItemId,
              secondLevelNestedFormItemId: hierarchy.secondLevelNestedFormItemId,
              formItemType: 'Records',
              values: Array.isArray(filterValue.value) ? (filterValue.value as string[]) : [filterValue.value as string],
            };
          }

          if (filterValue.formItemType === 'Date') {
            const dateValue = filterValue.value;
            let fromValue: string | undefined;
            let toValue: string | undefined;

            if (isDateFilterOption(dateValue)) {
              fromValue = !isValueEmpty(dateValue.fromDate) ? String(dateValue.fromDate) : undefined;
              toValue = !isValueEmpty(dateValue.toDate) ? String(dateValue.toDate) : undefined;
            } else if (dateValue) {
              const singleDate = typeof dateValue === 'string' ? dateValue : ((dateValue as FilterDate)?.date ?? String(dateValue));
              fromValue = singleDate;
              toValue = singleDate;
            }

            return {
              mainFormItemId: hierarchy.mainFormItemId,
              nestedFormItemId: hierarchy.nestedFormItemId,
              secondLevelNestedFormItemId: hierarchy.secondLevelNestedFormItemId,
              formItemType: filterValue.formItemType,
              fromValue,
              toValue,
              values: undefined,
            };
          }

          if (filterValue.formItemType === 'Number') {
            const numberValue = filterValue.value;
            let fromValue: string | undefined;
            let toValue: string | undefined;

            if (isNumberRangeFilterOption(numberValue)) {
              fromValue = !isValueEmpty(numberValue.fromNumber) ? String(numberValue.fromNumber) : undefined;
              toValue = !isValueEmpty(numberValue.toNumber) ? String(numberValue.toNumber) : undefined;
            } else if (!isValueEmpty(numberValue)) {
              const singleNumber = String(numberValue);
              fromValue = singleNumber;
              toValue = singleNumber;
            }

            return {
              mainFormItemId: hierarchy.mainFormItemId,
              nestedFormItemId: hierarchy.nestedFormItemId,
              secondLevelNestedFormItemId: hierarchy.secondLevelNestedFormItemId,
              formItemType: filterValue.formItemType,
              fromValue,
              toValue,
              values: undefined,
            };
          }

          return {
            mainFormItemId: hierarchy.mainFormItemId,
            nestedFormItemId: hierarchy.nestedFormItemId,
            secondLevelNestedFormItemId: hierarchy.secondLevelNestedFormItemId,
            formItemType: filterValue.formItemType,
            value: filterValue.value as string,
          };
        });
      }

      // 2-level structure for /Records endpoint
      return filterValuesToMap.map((filterValue) => {
        const filterItemFormItemId = filterValue.formItemId;
        const parentFormItemId = referencedFormItemsMap.get(filterItemFormItemId);
        const referencedFormItemId = parentFormItemId ? filterValue.formItemId : undefined;

        if (filterValue.formItemType === 'Tables' || filterValue.formItemType === 'nameArray') {
          return {
            formItemId: parentFormItemId || filterItemFormItemId,
            formItemType: 'Tables',
            values: Array.isArray(filterValue.value)
              ? (filterValue.value as unknown as ({ value: string } | null | undefined)[])
                  .filter((item): item is { value: string } => item != null)
                  .map((item) => item.value)
              : [filterValue.value as string],
            referencedFormItemId,
          };
        }

        if (filterValue.formItemType === 'Users') {
          return {
            formItemId: parentFormItemId || filterItemFormItemId,
            formItemType: 'Users',
            values: Array.isArray(filterValue.value) ? (filterValue.value as string[]) : [filterValue.value as string],
            referencedFormItemId,
          };
        }

        if (filterValue.formItemType === 'Records') {
          return {
            formItemId: parentFormItemId || filterItemFormItemId,
            formItemType: 'Records',
            values: Array.isArray(filterValue.value) ? (filterValue.value as string[]) : [filterValue.value as string],
            referencedFormItemId,
          };
        }

        if (filterValue.formItemType === 'Date') {
          const dateValue = filterValue.value;
          let fromValue: string | undefined;
          let toValue: string | undefined;

          if (isDateFilterOption(dateValue)) {
            fromValue = !isValueEmpty(dateValue.fromDate) ? String(dateValue.fromDate) : undefined;
            toValue = !isValueEmpty(dateValue.toDate) ? String(dateValue.toDate) : undefined;
          } else if (dateValue) {
            const singleDate = typeof dateValue === 'string' ? dateValue : ((dateValue as FilterDate)?.date ?? String(dateValue));
            fromValue = singleDate;
            toValue = singleDate;
          }

          return {
            formItemId: parentFormItemId || filterItemFormItemId,
            formItemType: filterValue.formItemType,
            fromValue,
            toValue,
            values: undefined,
            referencedFormItemId,
          };
        }

        if (filterValue.formItemType === 'Number') {
          const numberValue = filterValue.value;
          let fromValue: string | undefined;
          let toValue: string | undefined;

          if (isNumberRangeFilterOption(numberValue)) {
            fromValue = !isValueEmpty(numberValue.fromNumber) ? String(numberValue.fromNumber) : undefined;
            toValue = !isValueEmpty(numberValue.toNumber) ? String(numberValue.toNumber) : undefined;
          } else if (!isValueEmpty(numberValue)) {
            const singleNumber = String(numberValue);
            fromValue = singleNumber;
            toValue = singleNumber;
          }

          return {
            formItemId: parentFormItemId || filterItemFormItemId,
            formItemType: filterValue.formItemType,
            fromValue,
            toValue,
            values: undefined,
            referencedFormItemId,
          };
        }

        return {
          ...filterValue,
          formItemId: parentFormItemId || filterItemFormItemId,
          value: filterValue.value as string,
          referencedFormItemId,
        };
      });
    },
    [referencedFormItemsMap, shouldUse3Levels]
  );

  return mapFilterValues;
};
